/**
 * Durations are entered in hours (1.5, 0.25) but stored as whole minutes.
 *
 * Storing integer minutes keeps sums exact: adding a month of 0.1-style
 * decimals drifts, adding integers does not. Hours only ever exist at the
 * edges — the form field and the display.
 */

/** Time is logged in quarter-hour steps. */
export const INCREMENT_MINUTES = 15
export const INCREMENT_HOURS = INCREMENT_MINUTES / 60

/** Nobody logs more than a day against a single entry. */
export const MAX_ENTRY_MINUTES = 24 * 60

export function hoursToMinutes(hours: number): number {
  // Round before checking the increment: 1.5 * 60 is 90.00000000000001 in
  // binary floating point, and an unrounded comparison would reject it.
  return Math.round(hours * 60)
}

export function minutesToHours(minutes: number): number {
  return minutes / 60
}

export function isWholeIncrement(minutes: number): boolean {
  return Number.isInteger(minutes) && minutes % INCREMENT_MINUTES === 0
}

/** "1.5", "0.25", "2" — the number alone, trailing zeros trimmed. */
export function hoursValue(minutes: number): string {
  return String(Number(minutesToHours(minutes).toFixed(2)))
}

/**
 * "1.50", "0.25", "8.00" — always two decimals.
 *
 * For a grid of hours read as a column. Trimmed values ("1.5" above "8")
 * put the decimal point in a different place on every row, which is exactly
 * what makes a column of numbers hard to scan; a fixed width does not.
 */
export function hoursFixed(minutes: number): string {
  return minutesToHours(minutes).toFixed(2)
}

/** "1.5 h", "0.25 h", "2 h" — trailing zeros trimmed. */
export function formatHours(minutes: number): string {
  return `${hoursValue(minutes)} h`
}

export type ParsedDuration = { ok: true; minutes: number } | { ok: false; message: string }

/**
 * Validate a duration given in hours and convert it to stored minutes.
 * Accepts a number, or a numeric string (form fields send strings).
 */
export function parseHours(value: unknown): ParsedDuration {
  const hours = typeof value === 'string' ? Number(value.trim()) : value

  if (typeof hours !== 'number' || !Number.isFinite(hours)) {
    return { ok: false, message: 'hours must be a number' }
  }
  if (hours <= 0) {
    return { ok: false, message: 'hours must be greater than zero' }
  }

  const minutes = hoursToMinutes(hours)

  if (!isWholeIncrement(minutes)) {
    return {
      ok: false,
      message: `hours must be a multiple of ${INCREMENT_HOURS} (quarter-hour steps)`,
    }
  }
  if (minutes > MAX_ENTRY_MINUTES) {
    return { ok: false, message: `a single entry cannot exceed ${MAX_ENTRY_MINUTES / 60} hours` }
  }

  return { ok: true, minutes }
}

/** Clock time as `HH:MM`, what an `<input type="time">` gives back. */
export const CLOCK_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/

/** Minutes since midnight, or null if that is not a clock time. */
export function parseClock(value: string): number | null {
  const match = CLOCK_PATTERN.exec(value.trim())
  if (!match) return null
  return Number(match[1]) * 60 + Number(match[2])
}

/**
 * The duration between two clock times on the same day.
 *
 * Deliberately refuses to wrap past midnight. An entry belongs to one
 * calendar day, so 23:00 to 01:00 would have to put two hours somewhere it
 * did not happen; saying so is better than quietly filing it under the
 * wrong date. Somebody working through midnight logs two entries, which is
 * also what actually happened.
 *
 * The quarter-hour rule is the same one `parseHours` enforces, checked here
 * too so the form can say what is wrong before the request rather than
 * after — 09:00 to 09:50 is not a duration this app can store.
 */
export function minutesBetween(start: string, end: string): ParsedDuration {
  const from = parseClock(start)
  const to = parseClock(end)

  if (from === null || to === null) {
    return { ok: false, message: 'enter a start and an end time' }
  }
  if (to === from) {
    return { ok: false, message: 'the start and end times are the same' }
  }
  if (to < from) {
    return {
      ok: false,
      message: 'the end time is before the start — log time past midnight as two entries',
    }
  }

  const minutes = to - from

  if (!isWholeIncrement(minutes)) {
    return { ok: false, message: 'that comes to ' + minutes + ' minutes; use quarter hours' }
  }
  if (minutes > MAX_ENTRY_MINUTES) {
    return { ok: false, message: `a single entry cannot exceed ${MAX_ENTRY_MINUTES / 60} hours` }
  }

  return { ok: true, minutes }
}

/** `HH:MM`, `minutes` after midnight. Clamped to the end of the day. */
export function clockAfter(start: string, minutes: number): string {
  const from = parseClock(start) ?? 0
  const at = Math.min(from + minutes, 23 * 60 + 59)
  return `${String(Math.floor(at / 60)).padStart(2, '0')}:${String(at % 60).padStart(2, '0')}`
}

/**
 * A slice's share of a total, as it is written on screen.
 *
 * Rounds, but never down to "0%" for time that was genuinely logged: a real
 * fifteen minutes reported as nothing is worse than an imprecise "<1%".
 */
export function formatShare(minutes: number, total: number): string {
  if (total <= 0) return '—'
  const pct = Math.round((minutes / total) * 100)
  return pct === 0 && minutes > 0 ? '<1%' : `${pct}%`
}
